import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from '../../utils/axios'
import { InfoTooltip } from '../../components/atoms/info-tooltip/info-tooltip'
import CustomDropdown, { DropdownOption } from '../../components/molecules/dropdown/dropdown'
import EditableCell from '../../components/molecules/editable-cell/editable-cell'
import { BUSINESS_DETAILS } from '../../constants/routes'
import { t } from '../../utils/i18n'
import { useAuth } from '../../customHooks/useAuth'
import BusinessDiagnostics, {
  MetaData,
} from '../../components/organisms/business-diagnostics/business-diagnostics'
import { showToast } from '../../components/atoms/toaster/toaster'
import { BusinessAccountsContainer } from '../../containers/business-accounts-container'
import { mapCapsuleInfoWithMeta } from '../../containers/utils'
import { Button } from '../../components/atoms/button/button'
import { DeboardModal } from '../../components/organisms/DeboardModal/deboard-modal'
import { TokenHealth } from '../../components/organisms/token-health/TokenHealth'
export interface iBusiness {
  businessId: string
  businessName: string
  id?: string
}

export interface CapsuleInfo {
  id: number
  title: string
  subInfo: string
  toolTipContent?: string
  count?: number
}

export const BusinessDetails = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { id: businessIdParam } = useParams()
  const [businesses, setBusinesses] = useState<iBusiness[]>([])
  // eslint-disable-next-line
  const [selectedBusinessUser, setSelectedBusinessUser] = useState<any>([])
  const [isLoading, setIsLoading] = useState(true)
  const [capsuleInfo, setCapsuleInfo] = useState<CapsuleInfo[]>([])
  const [selectedTabId, setSelectedTabId] = useState<number>(1)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [shouldCallMetaInfoAPI, setShoulCallMetaInfo] = useState<number>(0)

  useEffect(() => {
    getBusinesses()
    getBusinessDetail(businessIdParam)
  }, [businessIdParam])

  const getBusinesses = async () => {
    try {
      const config = {
        method: 'GET',
        url: `${BUSINESS_DETAILS}/businessAccounts/records?status=MAPPED`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const businesses = await axios(config)
      setBusinesses(businesses?.data?.data)
      setIsLoading(false)
    } catch {
      setIsLoading(false)
    }
  }

  const getBusinessDetail = async (id?: string) => {
    try {
      const config = {
        method: 'GET',
        url: `${BUSINESS_DETAILS}/${id}/details`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const businessDetail = await axios(config)
      setSelectedBusinessUser(businessDetail?.data?.data)
      setIsLoading(false)
    } catch {
      setIsLoading(false)
    }
  }

  const handleTokenChange = async (uniqueId: string, businessID: string, newValue: string) => {
    try {
      const config = {
        method: 'PUT',
        url: `${BUSINESS_DETAILS}/${businessID}/edit`,
        data: {
          token: newValue,
        },
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const updatedResponse = await axios(config)
      if (updatedResponse?.data?.data?.status === true) {
        showToast({ message: t('token.change.success') })
      }
    } catch {
      showToast({ message: t('token.change.failure'), isError: true })
    }
  }

  const handleBusinessAccChange = (option: DropdownOption) => {
    if (option?.id !== businessIdParam) {
      setIsLoading(true)
      navigate(`/business-accounts/${option.id}`, { replace: true })
    } else {
      setIsLoading(false)
    }
  }

  const filterBusinessWithIDAndName = businesses?.map((business) => {
    return { id: business?.businessId, name: business?.businessName }
  })

  const renderLoadingBusinessSection = () => {
    return (
      <div className="dropdowns-loading-wrapper">
        <div className="dropdown-wrapper skeleton-block">
          <div className="title-info">
            <p className="label-input skeleton-text"></p>
          </div>
          <div className="skeleton-box"></div>
        </div>

        <div className="dropdown-wrapper edit-token skeleton-block">
          <div className="title-info">
            <p className=" skeleton-text"></p>
          </div>
          <div className="skeleton-box"></div>
        </div>
      </div>
    )
  }

  const metaInfo = (info: MetaData) => {
    setCapsuleInfo(mapCapsuleInfoWithMeta(info))
  }

  const callDeboardAPI = async (businessAccountId: string) => {
    try {
      const config = {
        method: 'POST',
        url: `${BUSINESS_DETAILS}/businessAccount/deboard?businessAccountId=${businessAccountId}`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const result = await axios(config)
      return result
    } catch {
      console.log('Deboard error')
    }
  }

  const deboardAsset = async (businessId: string) => {
    const result = await callDeboardAPI(businessId)
    if (result?.data?.data?.success) {
      showToast({
        message: t('account.deboard.success').replace('{{asset-id}}', t(`${businessId}`)),
      })
      navigate('/business-accounts')
    } else {
      showToast({
        message: t('account.deboard.fail').replace('{{asset-id}}', t(`${businessId}`)),
        isError: true,
      })
    }

    setModalOpen(false)
  }

  const callMetaAPIHandler = () => {
    setShoulCallMetaInfo(Date.now())
  }

  return (
    <div className="dashboard">
      <div className="dashboard-inner-container business-details ">
        <div className="top-section">
          <div className="top-left">
            {isLoading ? (
              renderLoadingBusinessSection()
            ) : (
              <>
                <div className="dropdown-wrapper">
                  {' '}
                  {/*NEEDS TO BE MODULARIZED*/}
                  <div className="title-info">
                    <p className="label-input">
                      {t('businessName')} <strong>*</strong>
                    </p>
                    <InfoTooltip
                      styleClass="input-info"
                      tooltipContent={t('businessName.dropdown.tooltip')}
                      placement={'right'}
                    />
                  </div>
                  <div className="dropdown">
                    <CustomDropdown
                      id="businessName-selector"
                      className="label-input"
                      label=""
                      defaultSelectedId={selectedBusinessUser?.id}
                      options={filterBusinessWithIDAndName}
                      onSelect={(option) => {
                        handleBusinessAccChange(option)
                      }}
                    />
                  </div>
                </div>
                <div className="dropdown-wrapper edit-token">
                  <div className="title-info">
                    <p className="label-input">
                      {t('token.dropdown')} <strong>*</strong>
                    </p>
                    <InfoTooltip
                      styleClass="input-info"
                      tooltipContent={t('token.dropdown.tooltip')}
                      placement={'right'}
                    />
                  </div>
                  <div className="editable-cell">
                    <EditableCell
                      rowData={[]}
                      initialValue={selectedBusinessUser?.token}
                      dataKey={selectedBusinessUser?.id}
                      onChange={(uniqueId: string, businessID: string, newValue: string) => {
                        handleTokenChange?.(uniqueId, businessID, newValue)
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="top-right">
            <div className="deboard-btn">
              <Button
                removeDefaultCss
                ariaLabel="deboard"
                handleClick={() => setModalOpen(true)}
                label={t('deboard.business.btn')}
              />
            </div>
          </div>
        </div>
        <hr className="link-divider divider" />
        <TokenHealth businessId={businessIdParam as string} />
        <BusinessDiagnostics
          businessData={selectedBusinessUser}
          isLoading={isLoading}
          getMetaInfo={metaInfo}
          passSelectedTabId={(id) => setSelectedTabId(id)}
          shouldCallMetaDataApi={shouldCallMetaInfoAPI}
        />
        {selectedTabId === 1 ? (
          <>
            <hr className="link-divider divider" />
            {businessIdParam ? (
              <BusinessAccountsContainer
                businessId={businessIdParam}
                capsuleInfo={capsuleInfo}
                callMetaAPIOnParent={callMetaAPIHandler}
              />
            ) : null}
          </>
        ) : null}
      </div>
      <DeboardModal
        modalTitle={t('deboardBusiness')}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        modalMessage={t('deboardModalMsgBusiness')}
        onDeboardClickHandler={() => deboardAsset(businessIdParam as string)}
      />
    </div>
  )
}

export default BusinessDetails
